import React, { useState } from 'react';

const AddDocumentModal = ({ 
  onSave, 
  onClose, 
  isLoading = false 
}) => {
  const [documentName, setDocumentName] = useState('');
  const [description, setDescription] = useState('');
  const [initialContent, setInitialContent] = useState('');
  const [documentType, setDocumentType] = useState('CUSTOM');
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!documentName.trim()) {
      newErrors.documentName = 'Le nom du document est requis';
    }
    
    if (!description.trim()) {
      newErrors.description = 'La description est requise';
    }
    
    if (!initialContent.trim()) {
      newErrors.initialContent = 'Le contenu initial est requis';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const documentData = {
      documentName: documentName.trim(),
      description: description.trim(),
      documentType,
      initialContent: initialContent.trim()
    };

    const success = await onSave(documentData);
    if (success) {
      // Réinitialiser le formulaire
      setDocumentName('');
      setDescription('');
      setInitialContent('');
      setDocumentType('CUSTOM');
      setErrors({});
    }
  };

  const handleClose = () => {
    // Réinitialiser le formulaire avant de fermer
    setDocumentName('');
    setDescription('');
    setInitialContent('');
    setDocumentType('CUSTOM');
    setErrors({});
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        {/* En-tête */}
        <div className="flex items-center justify-between pb-3 border-b">
          <h3 className="text-xl font-semibold text-gray-900">
            Ajouter un nouveau document
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isLoading}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          {/* Nom du document */}
          <div>
            <label htmlFor="documentName" className="block text-sm font-medium text-gray-700 mb-1">
              Nom du document *
            </label>
            <input
              type="text"
              id="documentName"
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.documentName ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Ex: Contrat de travail type"
              disabled={isLoading}
            />
            {errors.documentName && (
              <p className="mt-1 text-sm text-red-600">{errors.documentName}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description *
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.description ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Description du document et de son usage"
              disabled={isLoading}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description}</p>
            )}
          </div>

          {/* Type de document */}
          <div>
            <label htmlFor="documentType" className="block text-sm font-medium text-gray-700 mb-1">
              Type de document
            </label>
            <select
              id="documentType"
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isLoading}
            >
              <option value="CUSTOM">Document personnalisé</option>
              <option value="LEGAL">Document légal</option>
              <option value="CONTRACT">Contrat</option>
              <option value="PROCEDURE">Procédure</option>
              <option value="GUIDE">Guide</option>
              <option value="OTHER">Autre</option>
            </select>
          </div>

          {/* Contenu initial */}
          <div>
            <label htmlFor="initialContent" className="block text-sm font-medium text-gray-700 mb-1">
              Contenu initial *
            </label>
            <textarea
              id="initialContent"
              value={initialContent}
              onChange={(e) => setInitialContent(e.target.value)}
              rows={8}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.initialContent ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Contenu HTML ou texte du document. Vous pourrez le formater plus tard."
              disabled={isLoading}
            />
            {errors.initialContent && (
              <p className="mt-1 text-sm text-red-600">{errors.initialContent}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              Vous pouvez utiliser du HTML basique ou du texte brut. Le document sera formaté automatiquement.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              disabled={isLoading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Création...
                </div>
              ) : (
                'Créer le document'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDocumentModal;
